import React, { useState, useRef, useEffect } from 'react';
import { RabEntry } from '../../types';
import { generateRabSuggestion, generateCatalogFromDescription } from '../../services/geminiService';
import { ladePlanungsphaseFuerKi } from '../../services/aiAssistPlanung';
import type { Gebaeudetyp, Planungsphase } from '../planungsphase/types';
import { vorschlagsAnzeige, type VorschlagsAnzeige } from './ufsaKatalogVorschlag';
import { CHECKLIST_TEMPLATES, STANDARD_VORLAGE, vorlageAusGebaeudetyp } from './ufsaVorlagen';
import type { UfsaEintragVorbelegung } from './ufsaPlanung';
import { Sparkles, Plus, Loader2, Wand2, FileDown, FileUp, Download, AlertTriangle, ClipboardList, X } from 'lucide-react';
import { jsPDF } from "jspdf";
import * as pdfjsLib from "pdfjs-dist";
import pdfWorkerUrl from 'pdfjs-dist/build/pdf.worker.min.mjs?url';

// Fix for pdfjs-dist v4+ imports in Vite/ESM
const pdfjs = pdfjsLib;

if (pdfjs.GlobalWorkerOptions) {
  // Worker aus dem Bundle statt von unpkg: die feste CDN-Version lief der
  // lokalen Bibliothek nach jedem Update davon ("API version does not match
  // Worker version"), und offline gab es sie gar nicht.
  pdfjs.GlobalWorkerOptions.workerSrc = pdfWorkerUrl as string;
}

interface EntryFormProps {
  /**
   * `categoryByHand`: die Kategorie hat der Anwender selbst eingetippt oder
   * gewählt — die Ansicht übernimmt sie dann ohne Stichwortzuordnung.
   */
  onAdd: (entry: Omit<RabEntry, 'id'>, opts?: { categoryByHand?: boolean }) => void;
  onAddMultiple?: (entries: Omit<RabEntry, 'id'>[]) => void;
  existingEntries?: RabEntry[];
  /**
   * Projekt, dessen Planungsphase (Merkmale, Bauaufgabe) den Katalog-Prompt
   * ergaenzt. Optional: ohne Projekt oder ohne Planungsphase arbeitet der
   * Generator wie bisher allein aus der Beschreibung.
   */
  projectId?: string;
  /**
   * Gebaeudetyp aus dem Projektsteckbrief (Merkmal der Planungsphase) — nur
   * als VORAUSWAHL der Checklisten-Vorlage, solange der Nutzer nicht selbst
   * gewaehlt hat. `null`/fehlend: Vorgabe wie bisher.
   */
  gebaeudetyp?: Gebaeudetyp | null;
  /**
   * Vorbelegung des manuellen Eintrags aus einer Karte „Aus der Planungsphase".
   * Jeder neue `key` fuellt die Felder einmal; gespeichert wird erst mit
   * „Eintrag hinzufuegen".
   */
  vorbelegung?: UfsaEintragVorbelegung | null;
}

export const EntryForm: React.FC<EntryFormProps> = ({ onAdd, onAddMultiple, existingEntries = [], projectId, gebaeudetyp = null, vorbelegung = null }) => {
  const [component, setComponent] = useState('');
  const [category, setCategory] = useState('');
  // Kategoriefeld zuletzt vom Anwender geändert (nicht von KI-Vorschlag oder
  // Vorbelegung) — dann gilt seine Wahl beim Hinzufügen.
  const [categoryByHand, setCategoryByHand] = useState(false);
  const [activity, setActivity] = useState('');
  const [hazards, setHazards] = useState('');
  const [measures, setMeasures] = useState('');
  const [frequency, setFrequency] = useState('');
  const [regulations, setRegulations] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);

  const [projectDescription, setProjectDescription] = useState('');
  const [isBulkGenerating, setIsBulkGenerating] = useState(false);
  const [showBulkGen, setShowBulkGen] = useState(true);
  const [isReadingPdf, setIsReadingPdf] = useState(false);
  
  const [aiSuggestions, setAiSuggestions] = useState<Omit<RabEntry, 'id'>[] | null>(null);
  const [selectedSuggestions, setSelectedSuggestions] = useState<Set<number>>(new Set());
  // Herkunft der Vorschlagsliste: KI-Antwort oder gekennzeichneter Ersatz aus
  // dem Grundkatalog. Ein Ausfall darf nie wie eine Auswertung aussehen.
  const [anzeige, setAnzeige] = useState<VorschlagsAnzeige | null>(null);

  // Vorlage: die Wahl des Nutzers geht vor; sonst der Gebaeudetyp der
  // Planungsphase (`vorlageAusGebaeudetyp`); sonst die bisherige Vorgabe.
  // Abgeleitet statt in den Zustand kopiert — der Steckbrief kommt asynchron,
  // und ein Effekt, der den Zustand nachzieht, waere eine Kaskadenrenderung.
  const [templateWahl, setTemplateWahl] = useState<string | null>(null);
  const vorlageVorschlag = vorlageAusGebaeudetyp(gebaeudetyp);
  const selectedTemplate = templateWahl ?? vorlageVorschlag ?? STANDARD_VORLAGE;
  const vorlageAusPlanung = templateWahl === null && vorlageVorschlag !== null;
  const checklistInputRef = useRef<HTMLInputElement>(null);

  // Vorbelegung aus einer Karte „Aus der Planungsphase": waehrend des Renderns
  // uebernommen, sobald ein NEUER Schluessel ankommt (Muster „Zustand aus
  // vorherigem Render" — kein Effekt, kein zweiter Render-Durchlauf nach dem
  // Zeichnen). Danach gehoeren die Felder dem Nutzer.
  const [vorbelegungKey, setVorbelegungKey] = useState<string | null>(null);
  const [herkunft, setHerkunft] = useState<string | null>(null);
  if (vorbelegung && vorbelegung.key !== vorbelegungKey) {
    setVorbelegungKey(vorbelegung.key);
    setHerkunft(vorbelegung.herkunft);
    setComponent(vorbelegung.eintrag.component);
    setCategory(vorbelegung.eintrag.category);
    setCategoryByHand(false);
    setActivity(vorbelegung.eintrag.activity);
    setHazards(vorbelegung.eintrag.hazards);
    setMeasures(vorbelegung.eintrag.measures);
    setFrequency(vorbelegung.eintrag.frequency);
    setRegulations(vorbelegung.eintrag.regulations);
  }

  // Die Planungsphase des Projekts, fail-open geladen. Sie geht gefiltert
  // (Merkmale nur als ja/nein/ungeklaert, Freitext ohne Personendaten) als
  // Zusatzabschnitt in den Katalog-Prompt; `null` heisst: Prompt wie bisher.
  const [planung, setPlanung] = useState<Planungsphase | null>(null);
  useEffect(() => {
    let abgebrochen = false;
    if (!projectId) {
      setPlanung(null);
      return;
    }
    void (async () => {
      const p = await ladePlanungsphaseFuerKi(projectId);
      if (!abgebrochen) setPlanung(p);
    })();
    return () => { abgebrochen = true; };
  }, [projectId]);

  const handleAiGenerate = async () => {
    if (!component) return;
    setIsGenerating(true);
    try {
      const suggestion = await generateRabSuggestion(component);
      if (suggestion.component) setComponent(suggestion.component);
      if (suggestion.category) {
        setCategory(suggestion.category);
        setCategoryByHand(false);
      }
      if (suggestion.activity) setActivity(suggestion.activity);
      if (suggestion.hazards) setHazards(suggestion.hazards);
      if (suggestion.measures) setMeasures(suggestion.measures);
      if (suggestion.frequency) setFrequency(suggestion.frequency);
      if (suggestion.regulations) setRegulations(suggestion.regulations);
    } catch {
      alert("Fehler bei der KI-Generierung. Bitte versuchen Sie es erneut.");
    } finally {
      setIsGenerating(false);
    }
  };

  const handleBulkGenerate = async () => {
    if (!projectDescription || !onAddMultiple) return;
    setIsBulkGenerating(true);
    setAiSuggestions(null);
    setAnzeige(null);
    try {
      const ergebnis = await generateCatalogFromDescription(projectDescription, existingEntries, planung);
      if (ergebnis.eintraege.length > 0) {
        const a = vorschlagsAnzeige(ergebnis);
        setAiSuggestions(ergebnis.eintraege);
        setSelectedSuggestions(new Set(a.vorauswahl));
        setAnzeige(a);
      } else {
        alert("Die KI konnte keine neuen Einträge generieren. Bitte Beschreibung präzisieren.");
      }
    } catch {
      alert("Fehler bei der automatischen Generierung.");
    } finally {
      setIsBulkGenerating(false);
    }
  };

  const handleAddSelectedSuggestions = () => {
    if (!aiSuggestions || !onAddMultiple) return;
    const selected = aiSuggestions.filter((_, i) => selectedSuggestions.has(i));
    if (selected.length > 0) {
      onAddMultiple(selected);
    }
    setAiSuggestions(null);
    setAnzeige(null);
    setProjectDescription('');
    setShowBulkGen(false);
  };

  const handleCancelSuggestions = () => {
    setAiSuggestions(null);
    setAnzeige(null);
  };

  const toggleSuggestion = (index: number) => {
    const newSet = new Set(selectedSuggestions);
    if (newSet.has(index)) {
      newSet.delete(index);
    } else {
      newSet.add(index);
    }
    setSelectedSuggestions(newSet);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!component || !measures) {
      alert("Bitte mindestens Bauteil und Maßnahmen ausfüllen.");
      return;
    }
    onAdd({ component, category, activity, hazards, measures, frequency, regulations }, { categoryByHand });
    setComponent('');
    setCategory('');
    setCategoryByHand(false);
    setActivity('');
    setHazards('');
    setMeasures('');
    setFrequency('');
    setRegulations('');
    setHerkunft(null);
  };

  const downloadChecklist = () => {
    const tmpl = CHECKLIST_TEMPLATES[selectedTemplate];
    if (!tmpl) return;

    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    
    doc.setFontSize(16);
    doc.setFont("helvetica", "bold");
    doc.text(`CHECKLISTE: ${tmpl.label}`, 20, 20);
    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    doc.text("Markieren Sie vorhandene Bauteile durch Anklicken der Kästchen.", 20, 27);
    doc.setLineWidth(0.5);
    doc.line(20, 32, pageWidth - 20, 32);

    let y = 45;

    tmpl.items.forEach((item) => {
      doc.setFont("helvetica", "normal");
      doc.setFontSize(11);

      const textX = 32;
      const maxTextWidth = pageWidth - textX - 20; 
      const textLines = doc.splitTextToSize(item, maxTextWidth);
      const lineCount = textLines.length;
      
      const itemHeight = Math.max(7, lineCount * 5);

      if (y + itemHeight > pageHeight - 30) {
        doc.addPage();
        y = 20;
      }
      
      try {
        // @ts-expect-error AcroForm/GState members come from jsPDF plugins and are absent from its type definitions
        if (doc.AcroFormCheckBox) {
          // @ts-expect-error AcroForm/GState members come from jsPDF plugins and are absent from its type definitions
          const checkBox = new doc.AcroFormCheckBox();
          checkBox.caption = "4"; 
          checkBox.fieldName = item;
          checkBox.Rect = [20, y - 4, 5, 5];
          checkBox.appearanceState = "Off";
          doc.addField(checkBox);
          
          doc.rect(20, y - 4, 5, 5);
        } else {
           doc.setFont("courier", "normal");
           doc.text("[  ]", 20, y);
        }
      } catch {
         doc.setFont("courier", "normal");
         doc.text("[  ]", 20, y);
      }

      doc.setFont("helvetica", "normal");
      doc.setFontSize(11);
      doc.text(textLines, textX, y);
      
      y += itemHeight + 2;
    });

    if (y > pageHeight - 50) doc.addPage();
    
    y += 10;
    doc.line(20, y, pageWidth - 20, y);
    y += 10;
    doc.setFont("helvetica", "bold");
    doc.text("Sonstige Anmerkungen / Weitere Bauteile:", 20, y);
    
    try {
        // @ts-expect-error AcroForm/GState members come from jsPDF plugins and are absent from its type definitions
        if (doc.AcroFormTextField) {
             // @ts-expect-error AcroForm/GState members come from jsPDF plugins and are absent from its type definitions
             const textField = new doc.AcroFormTextField();
             textField.Rect = [20, y + 5, pageWidth - 40, 30];
             textField.multiline = true;
             textField.fieldName = "Notes";
             doc.addField(textField);
        }
    } catch {}

    doc.setDrawColor(200, 200, 200);
    doc.rect(20, y+5, pageWidth - 40, 30);

    doc.save(`Checkliste_${selectedTemplate}.pdf`);
  };

  const handleChecklistUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.type !== 'application/pdf') {
        alert("Bitte eine PDF-Datei hochladen.");
        return;
    }

    setIsReadingPdf(true);

    try {
        const arrayBuffer = await file.arrayBuffer();
        const loadingTask = pdfjs.getDocument(arrayBuffer);
        const pdf = await loadingTask.promise;
        
        const selectedItems: Set<string> = new Set();
        let customNotes = "";
        let fullText = ''; 

        for (let i = 1; i <= pdf.numPages; i++) {
            const page = await pdf.getPage(i);
            const annotations = await page.getAnnotations();
            
            for (const ann of annotations) {
                if (ann.subtype === 'Widget' && (ann.fieldType === 'Btn' || ann.checkBox)) {
                     const fieldName = ann.fieldName; 
                     const isChecked = ann.fieldValue && ann.fieldValue !== '/Off';
                     
                     if (isChecked && fieldName) {
                         selectedItems.add(fieldName);
                     }
                }
                
                if (ann.subtype === 'Widget' && ann.fieldType === 'Tx' && ann.fieldName === 'Notes') {
                    if (ann.fieldValue) customNotes += " " + ann.fieldValue;
                }
            }

            const content = await page.getTextContent();
            const strings = content.items.map((item: any) => item.str);
            fullText += strings.join(' ') + '\n';
        }

        if (selectedItems.size === 0) {
            const allPossibleItems = Object.values(CHECKLIST_TEMPLATES).flatMap(t => t.items);
            const normalizedText = fullText.replace(/\s+/g, ' '); 
            
            allPossibleItems.forEach(item => {
                 const escapedItem = item.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
                 const pattern = new RegExp(`(\\[\\s*[xX]\\s*\\]|\\(\\s*[xX]\\s*\\))\\s*${escapedItem}`, 'i');
                 
                 if (pattern.test(normalizedText)) {
                     selectedItems.add(item);
                 }
            });
        }
        
        if (!customNotes) {
            const notesHeader = "Sonstige Anmerkungen / Weitere Bauteile:";
            const notesIndex = fullText.indexOf(notesHeader);
            if (notesIndex !== -1) {
                let noteText = fullText.substring(notesIndex + notesHeader.length).trim();
                noteText = noteText.replace(/Seite \d+ \/ \d+/, '');
                if (noteText.length > 3) customNotes = noteText;
            }
        }

        const finalItems = Array.from(selectedItems);

        if (finalItems.length === 0 && !customNotes) {
             alert("Konnte keine Markierungen finden.\n\nTipp: Nutzen Sie die digitalen Checkboxen im PDF oder schreiben Sie ein 'x' in die Klammern.");
             if (fullText.length > 50) setProjectDescription(fullText);
        } else {
             const summary = `Gebäudeausstattung: ${finalItems.join(', ')}. ${customNotes ? 'Zusätzliche Hinweise: ' + customNotes : ''}`;
             setProjectDescription(summary);
        }

    } catch (err) {
        console.error("PDF Read Error", err);
        alert("Fehler beim Lesen der PDF-Datei.");
    } finally {
        setIsReadingPdf(false);
        e.target.value = '';
    }
  };

  return (
    <div className="mt-8 space-y-6">
      
      {onAddMultiple && (
        <div className="bg-gradient-to-r from-slate-800 to-blue-600 rounded-xl shadow-lg text-white overflow-hidden border border-white/10">
          <div 
            className="p-5 flex justify-between items-center cursor-pointer hover:bg-white/5 transition-colors"
            onClick={() => setShowBulkGen(!showBulkGen)}
          >
            <div className="flex items-center gap-3">
               <div className="bg-white/10 p-2.5 rounded-full border border-white/20 shadow-inner">
                 <Wand2 className="w-5 h-5" />
               </div>
               <div>
                 <h3 className="font-bold text-lg tracking-tight">Smart-Generator & Vorlagen</h3>
                 <p className="text-blue-100 text-sm opacity-80 font-medium">KI-Katalog aus Beschreibung oder PDF-Checkliste</p>
               </div>
            </div>
            <button className="bg-white/10 hover:bg-white/20 p-2 rounded-full transition-colors border border-white/10">
              <Plus className={`w-5 h-5 transition-transform duration-300 ${showBulkGen ? 'rotate-45' : ''}`} />
            </button>
          </div>

          {showBulkGen && (
            <div className="p-5 pt-0 animate-in slide-in-from-top-2 duration-300 space-y-6">
              
              <div className="space-y-3">
                 <label className="text-xs font-bold text-blue-200 uppercase tracking-widest block">
                    Option A: Manuelle Beschreibung
                 </label>
                 
                 {aiSuggestions ? (
                    <div className="bg-white rounded-xl shadow-lg p-4 text-slate-800">
                      <h3 className="font-bold text-lg mb-3">{anzeige?.titel ?? 'Gefundene Vorschläge'} ({aiSuggestions.length})</h3>
                      {anzeige?.hinweis && (
                        <div role="alert" className="mb-4 flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 p-3 text-amber-900">
                          <AlertTriangle className="w-5 h-5 shrink-0 mt-0.5" />
                          <div className="text-sm">
                            <div className="font-bold">{anzeige.hinweis}</div>
                            {anzeige.fehlerDetail && (
                              <div className="text-xs mt-1 text-amber-800">Meldung des Dienstes: {anzeige.fehlerDetail}</div>
                            )}
                          </div>
                        </div>
                      )}
                      <p className="text-sm text-slate-500 mb-4">Wählen Sie die zutreffenden Maßnahmen aus, um sie in den Katalog zu übernehmen.</p>

                      <div className="max-h-[300px] overflow-y-auto space-y-2 mb-4 border border-slate-100 rounded-lg p-2 bg-slate-50">
                        {aiSuggestions.map((suggestion, index) => (
                          <label key={index} className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${selectedSuggestions.has(index) ? 'bg-blue-50 border-blue-200' : 'bg-white border-slate-200 hover:bg-slate-50'}`}>
                            <div className="mt-0.5">
                              <input 
                                type="checkbox" 
                                checked={selectedSuggestions.has(index)}
                                onChange={() => toggleSuggestion(index)}
                                className="w-4 h-4 text-blue-600 rounded border-slate-300 focus:ring-blue-500"
                              />
                            </div>
                            <div className="flex-1 min-w-0">
                               <div className="font-bold text-sm text-slate-800">{suggestion.component}</div>
                               <div className="text-xs text-slate-500">{suggestion.activity}</div>
                            </div>
                          </label>
                        ))}
                      </div>
                      
                      <div className="flex justify-end gap-3 mt-4 pt-4 border-t border-slate-100">
                        <button onClick={handleCancelSuggestions} className="px-4 py-2 text-sm font-medium text-slate-600 hover:text-slate-800">Abbrechen</button>
                        <button onClick={handleAddSelectedSuggestions} disabled={selectedSuggestions.size === 0} className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-bold shadow-md disabled:opacity-50 transition-colors">Ausgewählte übernehmen ({selectedSuggestions.size})</button>
                      </div>
                    </div>
                 ) : (
                    <>
                     <textarea
                        value={projectDescription}
                        onChange={(e) => setProjectDescription(e.target.value)}
                        placeholder="Beschreiben Sie das Gebäude (z.B. 'Bürogebäude mit 5 Etagen, Flachdach, Aufzug und Tiefgarage')..."
                        className="w-full p-3 rounded-lg bg-white text-slate-900 focus:ring-2 focus:ring-blue-300 outline-none min-h-[100px] text-sm shadow-inner font-medium placeholder:text-slate-400"
                     />
                     <div className="flex justify-end">
                        <button
                          onClick={handleBulkGenerate}
                          disabled={!projectDescription || isBulkGenerating}
                          className="bg-white text-blue-700 hover:bg-blue-50 font-bold py-2 px-6 rounded-lg shadow-lg flex items-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed transition-all hover:scale-[1.02]"
                        >
                          {isBulkGenerating ? (
                            <>
                              <Loader2 className="w-4 h-4 animate-spin" />
                              Generiere Katalog...
                            </>
                          ) : (
                            <>
                              <Sparkles className="w-4 h-4" />
                              Automatisch erstellen
                            </>
                          )}
                        </button>
                     </div>
                    </>
                 )}
              </div>

              <div className="h-px bg-white/20 w-full"></div>

              <div className="space-y-3">
                 <label className="text-xs font-bold text-blue-200 uppercase tracking-widest block">
                    Option B: Interaktive Checkliste (PDF)
                 </label>
                 <div className="bg-white/10 rounded-lg p-4 border border-white/10 flex flex-col lg:flex-row gap-6 items-center justify-between">
                    
                    <div className="flex-1 w-full">
                        <div className="text-sm font-bold mb-2 flex items-center gap-2">
                            <FileDown className="w-4 h-4" /> 
                            1. Vorlage wählen & Laden
                        </div>
                        <div className="flex gap-2">
                            <select
                                value={selectedTemplate}
                                onChange={(e) => setTemplateWahl(e.target.value)}
                                aria-label="Vorlage der Checkliste"
                                className="bg-slate-700 border border-slate-600 text-white text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 outline-none font-medium"
                            >
                                {Object.entries(CHECKLIST_TEMPLATES).map(([key, tmpl]) => (
                                    <option key={key} value={key}>{tmpl.label}</option>
                                ))}
                            </select>
                            <button 
                                onClick={downloadChecklist}
                                className="bg-blue-600 hover:bg-blue-500 border border-blue-500 text-white p-2.5 rounded-lg transition-colors shadow-md"
                                title="Download als PDF"
                            >
                                <Download className="w-5 h-5" />
                            </button>
                        </div>
                        {vorlageAusPlanung && (
                            <p className="text-[11px] text-emerald-200 mt-1.5 flex items-center gap-1">
                                <ClipboardList className="w-3 h-3" /> Vorauswahl aus der Planungsphase (Gebäudetyp) — jederzeit änderbar.
                            </p>
                        )}
                    </div>

                    <div className="hidden lg:block text-white/30 font-bold text-2xl">→</div>
                    
                    <div className="flex-1 w-full">
                         <div className="text-sm font-bold mb-2 flex items-center gap-2">
                            <FileUp className="w-4 h-4" /> 
                            2. Ausgefüllte PDF importieren
                        </div>
                        <div 
                            onClick={() => !isReadingPdf && checklistInputRef.current?.click()}
                            className={`border-2 border-dashed border-white/30 hover:border-white/60 hover:bg-white/5 rounded-lg p-2 flex flex-col items-center justify-center cursor-pointer transition-all text-center h-[46px] ${isReadingPdf ? 'opacity-50 cursor-wait' : ''}`}
                        >
                            {isReadingPdf ? (
                                <span className="text-xs text-blue-200 font-bold flex items-center gap-2">
                                    <Loader2 className="w-4 h-4 animate-spin" /> Analysiere Formular...
                                </span>
                            ) : (
                                <span className="text-xs text-blue-200 font-bold">PDF Datei hier auswählen</span>
                            )}
                        </div>
                        <input 
                            type="file" 
                            ref={checklistInputRef}
                            accept=".pdf"
                            className="hidden"
                            onChange={handleChecklistUpload}
                            disabled={isReadingPdf}
                        />
                    </div>

                 </div>
                 <p className="text-[11px] text-blue-100/70 leading-relaxed max-w-2xl">
                    Die heruntergeladenen PDFs enthalten <strong>interaktive Formularfelder</strong>. Senden Sie diese an Ihre Kunden. 
                    Beim Re-Import erkennt das System gesetzte Häkchen automatisch und erstellt den Maßnahmenkatalog.
                 </p>
              </div>

            </div>
          )}
        </div>
      )}

      <div data-glass="karte" className="bg-white p-6 rounded-xl shadow-lg border border-slate-100 relative overflow-hidden">
        <div className="absolute top-0 left-0 w-1.5 h-full bg-blue-600"></div>
        <div className="flex justify-between items-center mb-6 pl-2">
          <h3 className="text-xl font-bold text-slate-800">Manueller Eintrag</h3>
          <span className="text-[11px] font-bold uppercase tracking-wider bg-blue-50 text-blue-600 px-2 py-1 rounded border border-blue-100">RAB 32 Konform</span>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5">

          {herkunft && (
            <div role="status" className="flex items-center justify-between gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-900">
              <span className="flex items-center gap-1.5">
                <ClipboardList className="w-3.5 h-3.5 shrink-0" />
                Vorbelegt aus der Planungsphase ({herkunft}) — prüfen, ergänzen und mit „Eintrag hinzufügen" speichern.
              </span>
              <button type="button" onClick={() => setHerkunft(null)} className="p-0.5 shrink-0" title="Hinweis ausblenden" aria-label="Hinweis ausblenden">
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          )}

          <div className="relative">
            <label htmlFor="catalog-component" className="block text-xs font-bold text-slate-500 uppercase mb-1.5">Bauteil / Anlage</label>
            <div className="flex gap-2">
              <input
                id="catalog-component"
                type="text"
                value={component}
                onChange={(e) => setComponent(e.target.value)}
                placeholder="z.B. Dachfenster, Aufzugsanlage, Fassade..."
                className="flex-1 p-3 border border-slate-300 rounded-lg shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
              />
              <button
                type="button"
                onClick={handleAiGenerate}
                disabled={!component || isGenerating}
                className={`px-5 py-2 rounded-lg flex items-center gap-2 font-medium transition-colors ${
                  !component || isGenerating 
                    ? 'bg-slate-100 text-slate-400 cursor-not-allowed border border-slate-200' 
                    : 'bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white shadow-md'
                }`}
              >
                {isGenerating ? <Loader2 className="w-5 h-5 animate-spin" /> : <Sparkles className="w-5 h-5" />}
                <span className="hidden sm:inline">KI-Vorschlag</span>
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div>
              <label className="block text-xs font-bold text-slate-500 uppercase mb-1.5">Kategorie</label>
              <input
                type="text"
                list="category-suggestions"
                value={category}
                onChange={(e) => {
                  setCategory(e.target.value);
                  setCategoryByHand(true);
                }}
                placeholder="z.B. Dach"
                className="w-full p-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
              />
              <datalist id="category-suggestions">
                <option value="Dach" />
                <option value="Fassade" />
                <option value="Außenanlage" />
                <option value="Technik" />
                <option value="Innenraum" />
                <option value="Verkehrswege" />
                <option value="Sonstiges" />
              </datalist>
            </div>
            <div>
              <label className="block text-xs font-bold text-slate-500 uppercase mb-1.5">Art der Arbeit</label>
              <input
                type="text"
                value={activity}
                onChange={(e) => setActivity(e.target.value)}
                className="w-full p-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
              />
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
             <div>
              <label className="block text-xs font-bold text-slate-500 uppercase mb-1.5">Turnus</label>
              <input
                type="text"
                value={frequency}
                onChange={(e) => setFrequency(e.target.value)}
                placeholder="z.B. Jährlich"
                className="w-full p-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
              />
            </div>
             <div>
              <label className="block text-xs font-bold text-slate-500 uppercase mb-1.5">Regelwerke / Vorschriften</label>
              <input
                type="text"
                value={regulations}
                onChange={(e) => setRegulations(e.target.value)}
                placeholder="z.B. ASR A2.1, DGUV V3"
                className="w-full p-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div>
              <label className="block text-xs font-bold text-slate-500 uppercase mb-1.5">Gefährdungen</label>
              <textarea
                rows={3}
                value={hazards}
                onChange={(e) => setHazards(e.target.value)}
                className="w-full p-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm outline-none transition-all"
                placeholder="z.B. Absturzgefahr..."
              />
            </div>
            <div>
              <label className="block text-xs font-bold text-slate-500 uppercase mb-1.5">Maßnahmen</label>
              <textarea
                rows={3}
                value={measures}
                onChange={(e) => setMeasures(e.target.value)}
                className="w-full p-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm outline-none transition-all"
                placeholder="z.B. Anschlagpunkte benutzen..."
              />
            </div>
          </div>

          <div className="flex justify-end pt-4 border-t border-slate-50">
            <button
              type="submit"
              className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-2.5 rounded-lg flex items-center gap-2 font-bold transition-all shadow-lg shadow-blue-200 hover:scale-[1.02]"
            >
              <Plus className="w-5 h-5" />
              Eintrag hinzufügen
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};